import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { resolveUrl } from "@/lib/api";
import { DEFAULT_HOME, mergeHome } from "@/lib/homeDefaults";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import JourneyCard from "@/components/JourneyCard";
import Row from "@/components/Row";
import { Play, Sparkles, Compass, Award } from "lucide-react";

const STAT_ICONS = [
    <Compass className="w-4 h-4" />,
    <Award className="w-4 h-4" />,
    <Sparkles className="w-4 h-4" />,
];

export default function Landing() {
    const [featured, setFeatured] = useState([]);
    const [freeJ, setFreeJ] = useState([]);
    // Start with DEFAULT_HOME so the page renders instantly even if /api/site/home is slow or fails.
    const [home, setHome] = useState(DEFAULT_HOME);

    useEffect(() => {
        api.get("/journeys", { params: { featured: true } }).then(r => setFeatured(r.data));
        api.get("/journeys", { params: { free: true } }).then(r => setFreeJ(r.data));
        api.get("/site/home").then(r => setHome(mergeHome(r.data?.content))).catch(() => {});
    }, []);

    const hero = home.hero;
    const featuredCfg = home.featured;
    const freeCfg = home.free_section;
    const manifesto = home.manifesto;
    const stats = hero.stats || [];

    return (
        <div className="min-h-screen relative grain">
            <Header />

            {/* Hero */}
            <section data-testid="hero-section" className="relative overflow-hidden">
                <div className="absolute inset-0 bg-[#0A0A0A]">
                    {hero.poster_url && (
                        <img
                            src={resolveUrl(hero.poster_url)}
                            alt=""
                            aria-hidden
                            className="absolute inset-0 w-full h-full object-cover"
                        />
                    )}
                    {hero.video_url && (
                        <video
                            data-testid="hero-video"
                            key={hero.video_url}
                            src={hero.video_url}
                            poster={hero.poster_url ? resolveUrl(hero.poster_url) : undefined}
                            autoPlay
                            muted
                            loop
                            playsInline
                            preload="auto"
                            className="absolute inset-0 w-full h-full object-cover"
                        />
                    )}
                </div>
                <div className="absolute inset-0 bg-gradient-to-b from-[#F8F8F5]/10 via-[#F8F8F5]/30 to-[#F8F8F5]/95" />
                <div className="absolute inset-0 bg-gradient-to-r from-[#F8F8F5]/85 via-[#F8F8F5]/35 to-transparent" />

                <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 pt-24 pb-32 md:pb-44">
                    <div className="max-w-3xl">
                        {hero.overline && <div className="overline text-[#1A1A1A] rise rise-1">{hero.overline}</div>}
                        <h1 className="font-display font-light text-5xl md:text-7xl leading-[0.95] mt-5 rise rise-2">
                            {hero.title_pre || ""}{hero.title_pre ? <br /> : null}
                            {hero.title_em && (
                                <span className="font-editorial italic font-normal text-holo text-6xl md:text-8xl block leading-[0.95]">
                                    {hero.title_em}
                                </span>
                            )}
                            {hero.title_post || ""}
                        </h1>
                        {hero.subtitle && (
                            <p className="font-body text-lg md:text-xl text-[#1A1A1A]/80 mt-8 max-w-xl rise rise-3">
                                {hero.subtitle}
                            </p>
                        )}
                        <div className="flex flex-wrap gap-4 mt-10 rise rise-4">
                            {hero.cta_primary_label && (
                                <Link to={hero.cta_primary_href || "/cadastro"} data-testid="hero-cta-trial" className="btn-primary">
                                    <Play className="w-4 h-4 fill-white" /> {hero.cta_primary_label}
                                </Link>
                            )}
                            {hero.cta_secondary_label && (
                                <Link to={hero.cta_secondary_href || "/jornadas"} data-testid="hero-cta-explore" className="btn-holo">
                                    <Sparkles className="w-4 h-4" /> {hero.cta_secondary_label}
                                </Link>
                            )}
                        </div>

                        {stats.length > 0 && (
                            <div className="mt-14 flex flex-wrap gap-8 rise rise-4">
                                {stats.map((s, i) => (
                                    <Stat key={i} icon={STAT_ICONS[i] || STAT_ICONS[0]} label={s.label} value={s.value} />
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </section>

            {/* Featured — grid with configurable columns */}
            <Row
                title={featuredCfg.title || "Jornadas em destaque"}
                eyebrow={featuredCfg.eyebrow || "Curadoria do Instituto"}
                testId="row-featured"
                columns={featuredCfg.columns || 4}
            >
                {featured.map(j => <JourneyCard key={j.id} journey={j} />)}
            </Row>

            {/* Free area */}
            {freeCfg.enabled !== false && freeJ.length > 0 && (
                <Row
                    title={freeCfg.title || "Comece gratuitamente"}
                    eyebrow={freeCfg.eyebrow || "Área aberta"}
                    testId="row-free"
                >
                    {freeJ.map(j => <JourneyCard key={j.id} journey={j} />)}
                </Row>
            )}

            {/* Manifesto */}
            {manifesto.enabled !== false && (
                <section className="max-w-[1400px] mx-auto px-6 md:px-10 mt-32">
                    <div className="rounded-[2rem] bg-[#0A0A0A] text-white p-10 md:p-20 relative overflow-hidden min-h-[520px]">
                        {manifesto.video_url && (
                            <video
                                data-testid="manifesto-video"
                                key={manifesto.video_url}
                                src={manifesto.video_url}
                                autoPlay
                                muted
                                loop
                                playsInline
                                preload="auto"
                                className="absolute inset-0 w-full h-full object-cover opacity-70"
                            />
                        )}
                        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A]/95 via-[#0A0A0A]/65 to-[#0A0A0A]/15" />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/85 via-transparent to-[#0A0A0A]/40" />

                        <div className="relative z-10 max-w-3xl">
                            <div className="overline text-white/60">Manifesto</div>
                            <h2 className="font-editorial italic text-4xl md:text-6xl leading-tight mt-4">
                                {manifesto.quote || "“Quando você muda a frequência da sua atenção, o corpo escuta. E a cura encontra rota.”"}
                            </h2>
                            <p className="font-body text-white/75 mt-6 max-w-xl">
                                {manifesto.tagline || "Instituto de Medicina Quântica — onde o rigor científico encontra a sabedoria contemplativa."}
                            </p>
                            <Link to={manifesto.cta_href || "/cadastro"} data-testid="manifesto-cta" className="btn-holo mt-10">
                                <Play className="w-4 h-4" /> {manifesto.cta_label || "Iniciar minha jornada"}
                            </Link>
                        </div>
                    </div>
                </section>
            )}

            <Footer />
        </div>
    );
}

function Stat({ icon, label, value }) {
    return (
        <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-white/70 backdrop-blur border border-black/5 flex items-center justify-center">
                {icon}
            </div>
            <div>
                <div className="font-display text-lg">{value}</div>
                <div className="font-body text-xs text-[#5C5C5C]">{label}</div>
            </div>
        </div>
    );
}
